import { useState, useEffect, useCallback } from 'react';

const LOCK_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const STORAGE_KEY = 'bear_last_active';

/**
 * Manages the PIN lock screen state.
 *
 * - Tracks user activity (touch/click/keydown) to reset the inactivity timer.
 * - After LOCK_TIMEOUT_MS of inactivity, sets `isLocked = true`.
 * - `unlock()` is called by PinLockScreen after PIN verification.
 * - `enabled` must be true (profile.pin_enabled) and the user must be logged in.
 */
export function usePinLock({ enabled = false } = {}) {
  const [isLocked, setIsLocked] = useState(false);

  const recordActivity = useCallback(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, Date.now().toString());
    } catch (_) {}
  }, []);

  const unlock = useCallback(() => {
    recordActivity();
    setIsLocked(false);
  }, [recordActivity]);

  useEffect(() => {
    if (!enabled) {
      setIsLocked(false);
      return;
    }

    // Check if already timed out since last visit
    try {
      const last = parseInt(sessionStorage.getItem(STORAGE_KEY) ?? '0', 10);
      if (last && Date.now() - last > LOCK_TIMEOUT_MS) {
        setIsLocked(true);
      }
    } catch (_) {}

    recordActivity();

    const events = ['touchstart', 'mousedown', 'keydown', 'scroll'];
    events.forEach((ev) => window.addEventListener(ev, recordActivity, { passive: true }));

    const interval = setInterval(() => {
      try {
        const last = parseInt(sessionStorage.getItem(STORAGE_KEY) ?? '0', 10);
        if (last && Date.now() - last > LOCK_TIMEOUT_MS) {
          setIsLocked(true);
        }
      } catch (_) {}
    }, 30_000); // check every 30s

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, recordActivity));
      clearInterval(interval);
    };
  }, [enabled, recordActivity]);

  return { isLocked, unlock };
}
